import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { branchApi } from "../Apis/Api";
import { MapContainer, TileLayer, FeatureGroup, useMap } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import LoadingComponent from "../components/ui/LoadingComponent";
import PropTypes from "prop-types";
import CustomSelect from "../components/common/CustomSelect";
import Breadcrumb from "../components/Breadcrumb";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";

// Country coordinates mapping
const COUNTRY_COORDINATES = {
  Pakistan: [30.3753, 69.3451],
  Oman: [21.5126, 55.9233],
  UAE: [24.4764, 54.3705],
};

// Search Control Component
const SearchControl = ({ onLocationFound }) => {
  const map = useMap();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchLocation = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5`
      );
      const results = await response.json();
      setSearchResults(results);
    } catch (error) {
      handleApiResponse(error, "Location search failed", "GET");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    map.setView([lat, lon], 15);
    L.marker([lat, lon]).addTo(map).bindPopup(result.display_name).openPopup();

    setSearchQuery("");
    setSearchResults([]);

    if (onLocationFound) {
      onLocationFound({ lat, lng: lon, name: result.display_name });
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => searchLocation(searchQuery), 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <div
      className="leaflet-top leaflet-right"
      style={{ margin: "10px 50px", zIndex: 1000 }}
    >
      <div
        className="leaflet-control"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ position: "relative", width: "300px" }}>
          <input
            type="text"
            placeholder="🔍 Search location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 40px 12px 16px",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              backgroundColor: "#fff",
              color: "#333",
            }}
            aria-label="Search location"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSearchResults([]);
              }}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
                color: "#666",
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
          {isSearching && (
            <div
              style={{
                padding: "12px",
                fontSize: "12px",
                color: "#666",
                backgroundColor: "#f8f9fa",
              }}
            >
              🔄 Searching...
            </div>
          )}
          {searchResults.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "white",
                borderRadius: "0 0 8px 8px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                maxHeight: "250px",
                overflowY: "auto",
                zIndex: 1001,
                marginTop: "2px",
              }}
            >
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  onClick={() => handleSearchSelect(result)}
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom:
                      index < searchResults.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "white")
                  }
                  role="option"
                  aria-selected={false}
                >
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: "13px",
                      color: "#333",
                    }}
                  >
                    📍 {result.name || result.display_name.split(",")[0]}
                  </div>
                  <div
                    style={{
                      color: "#666",
                      fontSize: "11px",
                      lineHeight: "1.3",
                    }}
                  >
                    {result.display_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SearchControl.propTypes = {
  onLocationFound: PropTypes.func,
};

// Current Location Marker Component
const CurrentLocationMarker = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (position) {
      const marker = L.marker(position, {
        icon: L.divIcon({
          className: "current-location-marker",
          html: `<div style="
            width: 20px;
            height: 20px;
            background: #4285F4;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          "></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10], // Center the icon
        }),
      }).addTo(map);

      marker.bindPopup("📍 Your Current Location").openPopup();

      // Add click event to zoom in
      marker.on("click", () => {
        map.setView(position, 16, {
          animate: true,
          duration: 0.5,
        });
        marker.openPopup();
      });

      // Add accuracy circle
      const circle = L.circle(position, {
        radius: 50,
        color: "#4285F4",
        fillColor: "#4285F4",
        fillOpacity: 0.1,
        weight: 1,
      }).addTo(map);

      return () => {
        map.removeLayer(marker);
        map.removeLayer(circle);
      };
    }
  }, [position, map]);

  return null;
};

CurrentLocationMarker.propTypes = {
  position: PropTypes.arrayOf(PropTypes.number),
};

// Map Component
const MapComponent = ({
  center,
  onPolygonCreated,
  onLocationFound,
  mapHeight = "400px",
  currentLocation,
}) => {
  const featureGroupRef = useRef();

  useEffect(() => {
    L.drawLocal.draw.toolbar.buttons.polygon = "Draw a location polygon";
    L.Draw.Polygon.prototype._getTooltipText = function () {
      return {
        text: this._initialLabelText || "Click to start drawing a polygon",
      };
    };
  }, []);

  return (
    <MapContainer
      center={center}
      zoom={13}
      style={{ height: mapHeight, width: "100%", borderRadius: "8px" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <SearchControl onLocationFound={onLocationFound} />
      {currentLocation && <CurrentLocationMarker position={currentLocation} />}
      <FeatureGroup ref={featureGroupRef}>
        <EditControl
          position="topright"
          onCreated={onPolygonCreated}
          draw={{
            polygon: {
              shapeOptions: {
                color: "#e50808ff",
                weight: 3,
                opacity: 0.8,
                fillColor: "#e50808ff",
                fillOpacity: 0.2,
              },
            },
            polyline: false,
            rectangle: false,
            circle: false,
            marker: false,
            circlemarker: false,
          }}
          edit={{ remove: true }}
        />
      </FeatureGroup>
    </MapContainer>
  );
};

MapComponent.propTypes = {
  center: PropTypes.arrayOf(PropTypes.number).isRequired,
  onPolygonCreated: PropTypes.func.isRequired,
  onLocationFound: PropTypes.func,
  mapHeight: PropTypes.string,
  currentLocation: PropTypes.arrayOf(PropTypes.number),
};

const BranchSelector = ({
  branches,
  selectedBranch,
  onBranchChange,
  isLoading,
  isDisabled,
  defaultBranch,
}) => {
  return (
    <div className="mb-3">
      <label htmlFor="branch_name" className="form-label fw-semibold">
        Branch Name
      </label>
      {false ? (
        <input
          type="text"
          className="form-control"
          id="branch_name"
          value={defaultBranch.name || ""}
          readOnly
          disabled
        />
      ) : (
        <CustomSelect
          options={branches}
          value={selectedBranch}
          onChange={onBranchChange}
          placeholder="Select a Branch"
          isDisabled={isLoading || isDisabled}
          isMulti={false}
          addNewLabel="Add New Branch"
          addNewRoute="/branch-create"
          noOptionsMessage={() => (
            <div>
              <p>No branches available</p>
              <Link to="/branch-create" className="text-primary-600">
                + Create New Branch
              </Link>
            </div>
          )}
        />
      )}
      {isLoading && !defaultBranch && (
        <small className="text-neutral-900">Loading branches...</small>
      )}
    </div>
  );
};

BranchSelector.propTypes = {
  branches: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  selectedBranch: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  onBranchChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  defaultBranch: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};

// Location Form Component
const LocationForm = ({ formData, onFormChange }) => (
  <form>
    <div className="row">
      <div className="col-md-6 mb-3">
        <BranchSelector
          branches={formData.branches}
          selectedBranch={formData.selectedBranch}
          onBranchChange={formData.onBranchChange}
          isLoading={formData.loadingBranches}
          isDisabled={formData.isBranchDisabled}
          defaultBranch={formData.defaultBranch}
        />
      </div>
      <div className="col-md-6 mb-3">
        <label htmlFor="location_name" className="form-label fw-semibold">
          Location Name
        </label>
        <input
          type="text"
          className="form-control"
          id="location_name"
          name="location_name"
          placeholder="Enter Location Name"
          value={formData.location_name}
          onChange={onFormChange}
          required
        />
      </div>
    </div>
    {/* <div className="row">
      <div className="col-12 mb-3">
        <label htmlFor="notes" className="form-label fw-semibold">
          Notes
        </label>
        <textarea
          className="form-control"
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={onFormChange}
          rows="4"
        />
      </div>
    </div> */}
  </form>
);

LocationForm.propTypes = {
  formData: PropTypes.shape({
    location_name: PropTypes.string,
    notes: PropTypes.string,
    polygons: PropTypes.arrayOf(
      PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      })
    ),
    branches: PropTypes.array,
    selectedBranch: PropTypes.object,
    onBranchChange: PropTypes.func,
    loadingBranches: PropTypes.bool,
    isBranchDisabled: PropTypes.bool,
    defaultBranch: PropTypes.object,
  }).isRequired,
  onFormChange: PropTypes.func.isRequired,
};

// Main Component
const AddBranchLocationPage = ({
  defaultBranch,
  onSubmit,
  mapHeight = "400px",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const branchData = defaultBranch || location.state?.branchData;
  const { navigationLink } = location.state || false;
  const [mapCenter, setMapCenter] = useState(COUNTRY_COORDINATES.UAE);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(
    branchData ? { value: branchData.id, label: branchData.name } : null
  );
  const [locationFormData, setLocationFormData] = useState({
    location_name: "",
    notes: "",
    polygons: [],
  });

  // Ensure selectedBranch is updated if branchData changes
  useEffect(() => {
    if (
      branchData &&
      (!selectedBranch || selectedBranch.value !== branchData.id)
    ) {
      setSelectedBranch({
        value: branchData.id,
        label: branchData.name,
      });
    }
  }, [branchData, selectedBranch]);

  // Fetch branches and set map focus based on country_name
  useEffect(() => {
    const fetchBranchesAndSetMap = async () => {
      if (!branchData) {
        setLoadingBranches(true);
        try {
          const response = await branchApi.getClientBranches({
            want_pagination: 0,
            sort_by: "branch_name",
            sort_order: "asc",
          });

          const formattedBranches = response.data.map((branch) => ({
            value: branch.branch_id,
            label: branch.branch_name,
          }));
          setBranches(formattedBranches);

          // Set map center based on the first branch's country_name
          if (response.data.length > 0) {
            const firstBranchCountry = response.data[0].country_name;
            const coordinates =
              COUNTRY_COORDINATES[firstBranchCountry] ||
              COUNTRY_COORDINATES.UAE;
            setMapCenter(coordinates);
          }
          handleApiResponse(response, "fetch branches", "GET");
        } catch (err) {
          handleApiResponse(err, "Failed to fetch branches", "GET");
          setMapCenter(COUNTRY_COORDINATES.UAE);
        } finally {
          setLoadingBranches(false);
        }
      } else {
        // Set map center based on branchData's country_name
        const coordinates =
          COUNTRY_COORDINATES[branchData.country_name] ||
          COUNTRY_COORDINATES.UAE;
        setMapCenter(coordinates);
      }
    };

    fetchBranchesAndSetMap();
  }, [branchData]);

  // Get current location and show on map
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const userLocation = [latitude, longitude];

          // Set current location marker
          setCurrentLocation(userLocation);

          // Only set map center to user location if no branch data
          if (!branchData && !branches.length) {
            setMapCenter(userLocation);
          }
        },
        (error) => {
          // Only show toast if we really need user location and no other fallback is available
          if (
            !branchData &&
            branches.length === 0 &&
            mapCenter === COUNTRY_COORDINATES.UAE
          ) {
            handleCustomResponse(
              "info",
              "Using default location. Enable location for better experience."
            );
          }
        }
      );
    }
  }, [branchData, branches, mapCenter]);

  const handleLocationFormChange = (e) => {
    const { name, value } = e.target;
    setLocationFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBranchChange = (selectedOption) => {
    setSelectedBranch(selectedOption);
  };

  const handlePolygonCreated = (e) => {
    const layer = e.layer;
    const latlngs = layer.getLatLngs()[0].map((point) => ({
      lat: point.lat,
      lng: point.lng,
    }));
    setLocationFormData((prev) => ({ ...prev, polygons: latlngs }));
  };

  const handleLocationFound = (locationData) => {
    setMapCenter([locationData.lat, locationData.lng]);
    if (!locationFormData.location_name) {
      setLocationFormData((prev) => ({
        ...prev,
        location_name: locationData.name,
      }));
    }
  };

  const handleLocationFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!branchData && !selectedBranch) {
      handleCustomResponse("error", "Please select a branch.");
      setLoading(false);
      return;
    }
    if (locationFormData.polygons.length === 0) {
      handleCustomResponse("error", "Please draw a polygon on the map.");
      setLoading(false);
      return;
    }
    try {
      const locationData = {
        branch_id: branchData?.id || selectedBranch?.value,
        location_name: locationFormData.location_name,
        notes: locationFormData.notes.trim() || null,
        polygons: locationFormData.polygons,
      };
      if (onSubmit) {
        await onSubmit(locationData);
      } else {
        const response = await branchApi.addBranchLocation(locationData);
        handleApiResponse(
          response,
          "Location details added successfully",
          "POST"
        );

        // Check if navigationLink exists and has a valid link
        if (navigationLink && navigationLink.link) {
          navigate(`/${navigationLink.link}`);
        } else {
          navigate(-1); // Fallback to previous page
        }
      }
      setLocationFormData({
        location_name: "",
        notes: "",
        polygons: [],
      });
    } catch (error) {
      handleApiResponse(error, "Failed to add location details", "POST");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loadingBranches && !branchData) {
    return (
      <>
        <Breadcrumb title="Add Location" />
        <LoadingComponent />
      </>
    );
  }

  return (
    <>
      <Breadcrumb title="Add Location" />
      <div
        className="card p-5 shadow-sm border-0"
        style={{ borderRadius: "12px" }}
      >
        <LocationForm
          formData={{
            location_name: locationFormData.location_name,
            notes: locationFormData.notes,
            polygons: locationFormData.polygons,
            branches,
            selectedBranch,
            onBranchChange: handleBranchChange,
            loadingBranches,
            isBranchDisabled: !!branchData,
            defaultBranch: branchData,
          }}
          onFormChange={handleLocationFormChange}
        />
        <div className="row">
          <div
            className="col-12 mb-3"
            style={{ position: "relative", zIndex: 1 }}
          >
            <label className="form-label fw-semibold">
              Search & Draw Location Polygon
              <small className="text-neutral-900 d-block">
                Search for a location or draw a polygon around the area
              </small>
            </label>
            <MapComponent
              center={mapCenter}
              onPolygonCreated={handlePolygonCreated}
              onLocationFound={handleLocationFound}
              mapHeight={mapHeight}
              currentLocation={currentLocation}
            />
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
          <CancelButtonForm />
          <UpdateButtonForm
            disabled={loading}
            onClick={handleLocationFormSubmit}
          />
        </div>
      </div>
    </>
  );
};

AddBranchLocationPage.propTypes = {
  defaultBranch: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
  mapHeight: PropTypes.string,
};

export default AddBranchLocationPage;
